import { FormContainer, LabeledInput, Worksheet } from "uikit"
import React from "react"
import { Dropdown, Input } from "semantic-ui-react"
import { useStore } from "store"
import { AddPersonModal } from "../../../people/AddPersonModal"
import { Footer } from "./Footer"

const PensionForm = ({
  data: {
    fields: { person_id, monthly_amount, description, cola },
    isModified,
    onSubmit,
    error
  },
  submitLabel = "Done",
  onDelete,
  peopleOptions,
  addNewPerson,
  isNew
}) => {
  return (
    <Worksheet error={error}>
      <Worksheet.Body>
        <Worksheet.Row label={"Description of pension"} paddedField>
          <Worksheet.Field error={description.error}>
            <Input {...description} autoComplete="off" />
          </Worksheet.Field>
        </Worksheet.Row>
        <Worksheet.Row label="Person receiving the pension benefit" paddedField>
          <Worksheet.Field error={person_id.error}>
            <Dropdown
              {...person_id}
              placeholder="Person"
              options={peopleOptions}
              selection
              data-testid="accountOwner"
            />
            {person_id.value === "__ADD__" && (
              <AddPersonModal
                submit={personValues =>
                  addNewPerson(personValues).then(({ personId }) =>
                    person_id.onChange(null, { value: personId })
                  )
                }
                onCancel={() => person_id.onChange(null, { value: null })}
              />
            )}
          </Worksheet.Field>
        </Worksheet.Row>
        <Worksheet.Row
          label="Monthly benefit amount"
          paddedField
          description="What is your monthly benefit amount?"
        >
          <Worksheet.Field error={monthly_amount.error}>
            <LabeledInput
              {...monthly_amount}
              label=""
              iconText="$"
              iconPosition="left"
              placeholder="0"
            />
          </Worksheet.Field>
        </Worksheet.Row>
        <Worksheet.Row
          label="Annual Cost-of-Living Adjustment"
          paddedField
          description={
            <>
              An annual percentage change applied to your retirement benefit to
              account for increasing prices or inflation. The Cost-of-Living
              Adjustment (COLA) is set by your pension plan and is typically
              between 0 and 3 percent.
            </>
          }
        >
          <Worksheet.Field error={cola.error}>
            <LabeledInput
              {...cola}
              label=""
              iconText="%"
              iconPosition="right"
              placeholder=""
            />
          </Worksheet.Field>
        </Worksheet.Row>
      </Worksheet.Body>
      <Footer
        isNew={isNew}
        submitLabel={submitLabel}
        onSubmit={onSubmit}
        onDelete={onDelete}
        isModified={isModified}
        deleteLabel="Delete Income"
        error={error}
      />
    </Worksheet>
  )
}

const PensionWorksheetFormContainer = FormContainer({
  description: {
    type: "string",
    defaultValue: ""
  },
  person_id: {
    type: "string"
  },
  monthly_amount: {
    type: "unit"
  },
  cola: {
    type: "unit",
    defaultValue: 0,
    minValue: 0,
    maxValue: 10
  },
  type: {
    type: "string",
    defaultValue: "pension"
  }
})(PensionForm)

const Pension = ({ household, income, ...formProps }) => {
  const [people, { addPerson }] = useStore("householdPeople", household.id)
  const isNew = income === null
  const values = isNew
    ? {
        person_id: people.length === 1 ? people[0].id : null,
        monthly_amount: null
      }
    : income

  const peopleOptions = Object.values(people)
    .filter(({ role }) => role === "spouse" || role === "client")
    .filter(
      ({ birth_date }) =>
        new Date().getFullYear() - new Date(birth_date).getFullYear() >= 55
    )
    .map(({ id: value, first_name: text }) => ({
      value,
      text
    }))

  const canAddPerson = false
  if (canAddPerson) {
    peopleOptions.push({ text: "Add person...", value: "__ADD__" })
  }
  const addNewPerson = values =>
    addPerson({ household_id: household.id, ...values })
  return (
    <PensionWorksheetFormContainer
      {...{
        values,
        peopleOptions,
        addNewPerson,
        ...formProps
      }}
    />
  )
}

export default Pension
